// src/pages/components/TaskCard.tsx
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MoreHorizontal, Pencil, Trash2, Calendar, Clock, AlertTriangle, CheckCircle2, Ban, PlayCircle, Briefcase, RotateCcw, FolderKanban } from 'lucide-react';
import { tasksApi, Task } from '../api/tasks';
import StatusBadge from './StatusBadge';
import { useUserInfo } from '../hooks/useUserInfo';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import TaskEdit from '../pages/Task/TaskEdit';
import { JourneyStrip } from './JourneyStrip';
import { displayedProgress } from '../utils/taskStatus';

interface TaskCardProps {
  task: Task;
  onRefresh: () => void;
}

function TaskCard({ task, onRefresh }: TaskCardProps) {
  const { guard, showError, showSuccess } = usePermissionGuard();
  const navigate = useNavigate();
  const [updating, setUpdating] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Custom confirmation modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showBlockPrompt, setShowBlockPrompt] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [pendingAction, setPendingAction] = useState<{ type: string; status?: string } | null>(null);

  const { getUserName, getUserRole, currentUser } = useUserInfo();

  const handleStatusQuickChange = async (e: React.MouseEvent, newStatus: string) => {
    e.stopPropagation();

    await guard(async () => {
      setUpdating(true);
      await tasksApi.changeStatus(task.id, { status: newStatus });
      showSuccess('Status Updated', `Task status changed to ${newStatus}`);
      onRefresh();
      setUpdating(false);
    }, 'CANNOT_MODIFY_OTHERS_TASK');
  };

  const handleDelete = async () => {
    await guard(async () => {
      await tasksApi.delete(task.id);
      showSuccess('Task Deleted', 'Task has been deleted successfully');
      onRefresh();
      setShowDeleteConfirm(false);
    }, 'CANNOT_DELETE_OTHERS_TASK');
  };

  // Stop a recurring task from spawning further copies. Keeps this task
  // (it becomes a normal one-off); already-spawned copies are unaffected.
  const handleStopRecurring = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await guard(async () => {
      await tasksApi.update(task.id, { is_recurring: false, recurrence_rule: null });
      showSuccess('Recurrence Stopped', 'This task will no longer repeat');
      onRefresh();
      setShowMenu(false);
    }, 'CANNOT_MODIFY_OTHERS_TASK');
  };

  const handleComplete = async () => {
    await guard(async () => {
      await tasksApi.changeStatus(task.id, { status: 'DONE' });
      showSuccess('Task Completed', 'Task has been marked as completed');
      onRefresh();
      setShowCompleteConfirm(false);
    }, 'CANNOT_MODIFY_OTHERS_TASK');
  };

  const handleReopen = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await guard(async () => {
      await tasksApi.reopen(task.id);
      showSuccess('Task Reopened', 'Task moved back to TODO');
      onRefresh();
    }, 'CANNOT_MODIFY_OTHERS_TASK');
  };

  const handleBlock = async () => {
    if (!blockReason.trim()) {
      showError('CANNOT_BLOCK_WITHOUT_REASON', 'Please provide a reason for putting this task on hold');
      return;
    }

    await guard(async () => {
      await tasksApi.changeStatus(task.id, { status: 'BLOCKED', blocked_reason: blockReason });
      showSuccess('Task on Hold', 'Task has been put on hold');
      onRefresh();
      setShowBlockPrompt(false);
      setBlockReason('');
    }, 'CANNOT_MODIFY_OTHERS_TASK');
  };

  const handleQuickAction = (e: React.MouseEvent, action: string) => {
    e.stopPropagation();
    setPendingAction({ type: action });

    switch (action) {
      case 'start':
        handleStatusQuickChange(e, 'IN_PROGRESS');
        break;
      case 'complete':
        setShowCompleteConfirm(true);
        break;
      case 'block':
        setShowBlockPrompt(true);
        break;
      default:
        break;
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'No due date';
    const date = new Date(dateStr);
    const today = new Date();
    const diffDays = Math.ceil((date.getTime() - today.getTime()) / (1000 * 3600 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    return date.toLocaleDateString();
  };

  const isOverdue = task.is_overdue && task.status !== 'DONE' && task.status !== 'CANCELLED';
  const isCompleted = task.status === 'DONE';
  const isAssignedToMe = task.primary_assignee_id === currentUser?.id;
  const isElevated = ['admin', 'manager', 'hr head', 'hr admin', 'founder', 'director'].includes(currentUser?.role || '');
  const isCreator = task.created_by === currentUser?.id;
  const canDelete = isCreator || isElevated;
  const canStart = task.status === 'TODO' && (isAssignedToMe || isCreator || isElevated);

  const priorityPill: Record<string, string> = {
    URGENT: 'gt-pill is-urgent',
    HIGH:   'gt-pill is-high',
    MEDIUM: 'gt-pill is-medium',
    LOW:    'gt-pill is-low',
  };

  const priorityBar: Record<string, string> = {
    URGENT: 'is-urgent',
    HIGH:   'is-high',
    MEDIUM: 'is-medium',
    LOW:    'is-low',
  };

  return (
    <>
      <div
  className={`relative transition-all duration-300 ${
    expanded ? 'z-30' : 'z-10'
  }`}
  style={{ alignSelf: 'start' }}
>
        <div
          onClick={() => navigate(`/tasks/${task.id}`)}
          onMouseEnter={() => setExpanded(true)}
          onMouseLeave={() => { setExpanded(false); setShowMenu(false); }}
          className={`group gt-task-card cursor-pointer overflow-hidden p-5
transition-all duration-300 origin-center
${expanded ? 'scale-[1.03] z-20 shadow-2xl' : 'scale-100'}
is-prio-${(task.priority || 'medium').toLowerCase()}
${isCompleted ? 'is-done' : ''}
${isOverdue ? 'is-overdue' : ''}
`}
          style={{
  display: 'flex',
  flexDirection: 'column',
  minHeight: expanded ? 430 : 310,
  height: expanded ? 'auto' : 310,
  transition: 'all 260ms cubic-bezier(0.33, 1, 0.68, 1)',
}}
        >
          <div className={`gt-prio-bar ${priorityBar[task.priority] || 'is-medium'}`} />

          {isCompleted && (
            <div className="gt-completed-badge">
              <CheckCircle2 className="w-3 h-3" strokeWidth={3} />
              Completed
            </div>
          )}

          {(task.code || task.project_code) && (
            <div className={`flex items-center gap-1.5 mb-1.5 pl-2 flex-wrap ${isCompleted ? 'pt-7' : ''}`}>
              {task.code && (
                <span
                  title={`Task ID · ${task.code}`}
                  style={{
                    fontFamily: 'var(--gt-font-mono)',
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    padding: '2px 7px',
                    borderRadius: 5,
                    background: 'var(--gt-primary-container)',
                    color: 'var(--gt-primary-deep)',
                    border: '1px solid color-mix(in srgb, var(--gt-primary) 25%, transparent)',
                  }}
                >
                  {task.code}
                </span>
              )}
              {task.project_code && (
                <span
                  title={`Project · ${task.project_code}`}
                  style={{
                    fontFamily: 'var(--gt-font-mono)',
                    fontSize: 10.5,
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    padding: '2px 7px',
                    borderRadius: 5,
                    background: 'var(--gt-surface-2)',
                    color: 'var(--gt-text-secondary)',
                    border: '1px solid var(--gt-border)',
                  }}
                >
                  {task.project_code}
                </span>
              )}
            </div>
          )}

          <div className={`flex justify-between items-start mb-3 pl-2 ${isCompleted && !task.code && !task.project_code ? 'pt-7' : ''}`}>
            <h3
              className="gt-display flex-1 pr-2 line-clamp-2"
              style={{
                fontSize: 15, fontWeight: 700, lineHeight: 1.3,
                color: isCompleted ? 'var(--gt-text-disabled)' : 'var(--gt-text-primary)',
                textDecoration: isCompleted ? 'line-through' : 'none',
              }}
            >
              {task.title}
            </h3>

            <button
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
              className="gt-icon-btn is-sm"
              style={{ opacity: 1 }}
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>

            {showMenu && (
              <div className="absolute right-4 top-12 z-10 min-w-[140px] gt-dropdown gt-pop-in">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowEditModal(true); setShowMenu(false); }}
                  className="gt-dropdown-item"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Edit
                </button>
                {task.is_recurring && (
                  <button
                    onClick={(e) => handleStopRecurring(e)}
                    className="gt-dropdown-item"
                  >
                    <Ban className="w-3.5 h-3.5" />
                    Stop recurring
                  </button>
                )}
                {canDelete && (
                  <>
                    <div className="gt-dropdown-divider" />
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(true); setShowMenu(false); }}
                      className="gt-dropdown-item"
                      style={{ color: 'var(--gt-dnd)' }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-1.5 mb-3 flex-wrap pl-2">
            <StatusBadge status={task.status} />
            <span className={priorityPill[task.priority] || 'gt-pill is-ghost'}>
              <span className="gt-dot" />
              {task.priority}
            </span>
            {isOverdue && (
              <span className="gt-pill is-overdue">
                <AlertTriangle className="w-3 h-3" />
                Overdue
              </span>
            )}
            {task.is_recurring && (
              <span className="gt-pill is-ghost" title="Repeats on a schedule">
                ↻ Recurring
              </span>
            )}
          </div>

          {task.primary_assignee_id && (
            <div className="flex items-center gap-2 mb-3 pl-2">
              <div className="gt-avatar" style={{ width: 22, height: 22, fontSize: 10 }}>
                {(task.primary_assignee_name || getUserName(task.primary_assignee_id)).charAt(0).toUpperCase()}
              </div>
              <span className="gt-page-sub">
                {task.primary_assignee_name || getUserName(task.primary_assignee_id)}
                {isAssignedToMe && <em className="gt-serif-italic" style={{ marginLeft: 6, color: 'var(--gt-accent-strong)' }}>· you</em>}
              </span>
              {task.primary_assignee_id && (
                <span className="gt-eyebrow" style={{ marginBottom: 0, fontSize: 9 }}>
                  · {getUserRole(task.primary_assignee_id)}
                </span>
              )}
            </div>
          )}

          {expanded && task.description && (
            <p
              className="pl-2 line-clamp-4"
              style={{
                fontSize: 12.5,
                color: 'var(--gt-text-tertiary)',
                marginBottom: 12,
                wordBreak: 'break-word',
              }}
              title={task.description}
            >
              {task.description}
            </p>
          )}

          {expanded && task.tags && task.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3 pl-2">
              {task.tags.slice(0, 6).map((tag, i) => (
                <span key={i} className="gt-pill is-ghost" style={{ fontSize: 10 }}>#{tag}</span>
              ))}
              {task.tags.length > 6 && (
                <span className="gt-pill is-ghost" style={{ fontSize: 10 }}>+{task.tags.length - 6}</span>
              )}
            </div>
          )}

          <div className="mb-3 pl-2">
            <div className="flex justify-between gt-page-sub mb-1.5 gt-numerals" style={{ fontSize: 11 }}>
              <span className="gt-eyebrow" style={{ marginBottom: 0 }}>Progress</span>
              <span className="gt-display" style={{ color: 'var(--gt-accent-strong)' }}>{displayedProgress(task.progress_percent, task.status)}%</span>
            </div>
            <div className="gt-progress-track">
              <div
                className="gt-progress-fill"
                style={{ width: `${displayedProgress(task.progress_percent, task.status)}%` }}
              />
            </div>
          </div>

          {expanded && (
            <div className="mb-3 pl-2 p-2.5" style={{ background: 'var(--gt-surface-2)', borderRadius: 10 }}>
              <JourneyStrip
                currentStatus={task.status}
                createdBy={getUserName(task.created_by)}
                assigneeName={getUserName(task.primary_assignee_id)}
              />
            </div>
          )}

          <div className="mt-auto flex justify-between items-center pt-3 pl-2" style={{ borderTop: '1px dashed var(--gt-border-strong)' }}>
            <div className="flex items-center gap-1.5 gt-page-sub gt-numerals">
              <Calendar className="w-3.5 h-3.5" />
              <span style={{ color: isOverdue ? 'var(--gt-prio-urgent-fg)' : undefined, fontWeight: isOverdue ? 600 : 400 }}>
                {formatDate(task.due_date)}
              </span>
            </div>

            <div className="flex gap-1.5">
              {task.status === 'TODO' && canStart && (
                <button
                  onClick={(e) => handleQuickAction(e, 'start')}
                  disabled={updating}
                  className="gt-btn is-primary is-sm"
                >
                  {updating ? 'Starting…' : (<><PlayCircle className="w-3.5 h-3.5" /> Start</>)}
                </button>
              )}

              {task.status === 'IN_PROGRESS' && (
                <button onClick={(e) => handleQuickAction(e, 'complete')} className="gt-btn is-accent is-sm">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Complete
                </button>
              )}

              {task.status === 'IN_PROGRESS' && (
                <button onClick={(e) => handleQuickAction(e, 'block')} className="gt-btn is-danger is-sm">
                  <Ban className="w-3.5 h-3.5" /> Hold
                </button>
              )}

              {(task.status === 'DONE' || task.status === 'CANCELLED') && (
                <button onClick={handleReopen} className="gt-btn is-ghost is-sm">
                  <RotateCcw className="w-3.5 h-3.5" /> Reopen
                </button>
              )}
            </div>
          </div>

          {expanded && (
            <div className="mt-3 pl-2 flex items-center gap-2 gt-page-sub gt-numerals" style={{ fontSize: 10.5 }}>
              <Clock className="w-3 h-3" />
              <span>Created {formatDate(task.created_at)}</span>
              {task.created_by && (
                <span>· <em className="gt-serif-italic">by</em> {task.created_by_name || getUserName(task.created_by)}</span>
              )}
            </div>
          )}
          {(task.client_name || task.project_name) && (
            <div className="flex flex-wrap gap-1.5 mt-2 ml-2">
              {task.client_name && (
                <div
                  className="inline-flex items-center gap-1.5"
                  style={{
                    padding: '3px 9px',
                    background: 'var(--gt-primary-container)',
                    color: 'var(--gt-primary-deep)',
                    borderRadius: 6,
                    border: '1px solid color-mix(in srgb, var(--gt-primary) 25%, transparent)',
                    fontFamily: 'var(--gt-font-display)',
                    fontWeight: 600,
                    fontSize: 10.5,
                    letterSpacing: '0.01em',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={task.client_name}
                >
                  <Briefcase className="w-3 h-3 flex-shrink-0" strokeWidth={2} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{task.client_name}</span>
                </div>
              )}
              {task.project_name && (
                <div
                  className="inline-flex items-center gap-1.5"
                  style={{
                    padding: '3px 9px',
                    background: 'var(--gt-surface-2)',
                    color: 'var(--gt-text-primary)',
                    borderRadius: 6,
                    border: '1px solid var(--gt-border)',
                    fontFamily: 'var(--gt-font-display)',
                    fontWeight: 600,
                    fontSize: 10.5,
                    letterSpacing: '0.01em',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={task.project_name}
                >
                  <FolderKanban className="w-3 h-3 flex-shrink-0" strokeWidth={2} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{task.project_name}</span>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {showDeleteConfirm && createPortal(
        <div className="gt-modal-backdrop flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="gt-modal w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6" style={{ borderBottom: '1px solid var(--gt-divider)' }}>
              <div className="flex items-center gap-3">
                <span style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--gt-status-overdue-bg)', color: 'var(--gt-prio-urgent-fg)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Trash2 className="w-5 h-5" />
                </span>
                <h3 className="gt-display" style={{ fontSize: 18 }}>Delete <em className="gt-serif-italic">Task</em></h3>
              </div>
            </div>
            <div className="p-6">
              <p className="gt-page-sub">Are you sure you want to delete <em className="gt-serif-italic" style={{ color: 'var(--gt-text-primary)' }}>"{task.title}"</em>?</p>
              <p className="gt-page-sub mt-2" style={{ color: 'var(--gt-prio-urgent-fg)' }}>This action cannot be undone.</p>
            </div>
            <div className="p-6 flex justify-end gap-3" style={{ borderTop: '1px solid var(--gt-divider)' }}>
              <button onClick={() => setShowDeleteConfirm(false)} className="gt-btn is-ghost">Cancel</button>
              <button onClick={handleDelete} className="gt-btn is-danger">Delete Task</button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {showCompleteConfirm && createPortal(
        <div className="gt-modal-backdrop flex items-center justify-center p-4" onClick={() => setShowCompleteConfirm(false)}>
          <div className="gt-modal w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6" style={{ borderBottom: '1px solid var(--gt-divider)' }}>
              <div className="flex items-center gap-3">
                <span style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--gt-status-done-bg)', color: 'var(--gt-accent-strong)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckCircle2 className="w-5 h-5" />
                </span>
                <h3 className="gt-display" style={{ fontSize: 18 }}>Complete <em className="gt-serif-italic">Task</em></h3>
              </div>
            </div>
            <div className="p-6">
              <p className="gt-page-sub">Mark <em className="gt-serif-italic" style={{ color: 'var(--gt-text-primary)' }}>"{task.title}"</em> as completed?</p>
            </div>
            <div className="p-6 flex justify-end gap-3" style={{ borderTop: '1px solid var(--gt-divider)' }}>
              <button onClick={() => setShowCompleteConfirm(false)} className="gt-btn is-ghost">Cancel</button>
              <button onClick={handleComplete} className="gt-btn is-accent">Complete</button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {showBlockPrompt && createPortal(
        <div className="gt-modal-backdrop flex items-center justify-center p-4" onClick={() => setShowBlockPrompt(false)}>
          <div className="gt-modal w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6" style={{ borderBottom: '1px solid var(--gt-divider)' }}>
              <div className="flex items-center gap-3">
                <span style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--gt-status-overdue-bg)', color: 'var(--gt-prio-urgent-fg)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Ban className="w-5 h-5" />
                </span>
                <h3 className="gt-display" style={{ fontSize: 18 }}>Hold <em className="gt-serif-italic">Task</em></h3>
              </div>
            </div>
            <div className="p-6 space-y-3">
              <p className="gt-page-sub">Reason for putting <em className="gt-serif-italic" style={{ color: 'var(--gt-text-primary)' }}>"{task.title}"</em> on hold:</p>
              <textarea
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Enter reason for hold…"
                rows={3}
                className="gt-textarea"
                style={{ resize: 'none' }}
                autoFocus
              />
            </div>
            <div className="p-6 flex justify-end gap-3" style={{ borderTop: '1px solid var(--gt-divider)' }}>
              <button onClick={() => { setShowBlockPrompt(false); setBlockReason(''); }} className="gt-btn is-ghost">Cancel</button>
              <button onClick={handleBlock} className="gt-btn is-danger">Hold Task</button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Edit Task Modal */}
      <TaskEdit
        isOpen={showEditModal}
        task={task}
        onClose={() => setShowEditModal(false)}
        onSuccess={onRefresh}
      />
    </>
  );
}

export default TaskCard;